<template>
  <v-dialog
    id="reservations-upload-modal"
    v-model="uploadModal"
    max-width="715px"
  >
    <v-stepper
      v-model="uploadStep"
      vertical
    >
      <v-stepper-step
        step="1"
        :complete="uploadStep > 1"
      >
        Upload CSV
      </v-stepper-step>

      <v-stepper-content step="1">
        <v-form v-model="valid">
          <v-file-input
            v-model="uploadedCSV"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            label="Select file"
            outlined
            filled
            required
            prepend-icon=""
            prepend-inner-icon="$faFileSpreadsheet"
            :rules="[v => !!v || 'CSV is required']"
            class="my-10"
          />
        </v-form>
        <v-btn
          color="primary"
          :disabled="!valid"
          @click="validateCSV"
        >
          Continue
        </v-btn>
        <v-btn
          text
          @click="() => this.$emit('close-upload-modal')"
        >
          Cancel
        </v-btn>
      </v-stepper-content>

      <v-stepper-step
        step="2"
        :complete="uploadStep > 2"
      >
        Validate
      </v-stepper-step>

      <v-stepper-content step="2">
        <div>
          <div v-if="validationMessage.data.goodReservations">
            <p class="font-weight-bold">
              Reservations to be uploaded:
            </p>
            <v-simple-table
              fixed-header
              height="200px"
              dense
              class="validation-table"
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      Property Name
                    </th>
                    <th class="text-left">
                      Property ID
                    </th>
                    <th class="text-left">
                      Checkin
                    </th>
                    <th class="text-left">
                      Checkout
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in validationMessage.data.goodReservations"
                    :key="item.name"
                  >
                    <td>{{ item.propertyName }}</td>
                    <td>{{ item.propertyID }}</td>
                    <td>{{ formatDate(item.checkin, item.timezone) }}</td>
                    <td>{{ formatDate(item.checkout, item.timezone) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <div v-if="validationMessage.data.badReservations">
            <p class="font-weight-bold">
              Reservations that did not pass validation:
            </p>
            <v-simple-table
              fixed-header
              height="200px"
              dense
              class="validation-table"
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      Property Name
                    </th>
                    <th class="text-left">
                      Property ID
                    </th>
                    <th class="text-left">
                      Checkin
                    </th>
                    <th class="text-left">
                      Checkout
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in validationMessage.data.badReservations"
                    :key="item.name"
                  >
                    <td>{{ item.propertyName }}</td>
                    <td>{{ item.propertyID }}</td>
                    <td>{{ formatDate(item.checkin, item.timezone) }}</td>
                    <td>{{ formatDate(item.checkout, item.timezone) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <div v-if="validationMessage.data.reservationsToDelete">
            <p class="font-weight-bold">
              Reservations to be deleted:
            </p>
            <v-simple-table
              fixed-header
              height="200px"
              dense
              class="validation-table"
            >
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      Reservation ID
                    </th>
                    <th class="text-left">
                      Property ID
                    </th>
                    <th class="text-left">
                      Checkin
                    </th>
                    <th class="text-left">
                      Checkout
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in validationMessage.data.reservationsToDelete"
                    :key="item.name"
                  >
                    <td>{{ item.id }}</td>
                    <td>{{ item.propertyID }}</td>
                    <td>{{ formatDate(item.checkin, item.timezone) }}</td>
                    <td>{{ formatDate(item.checkout, item.timezone) }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
        </div>
        <div v-if="validationMessage.errors">
          <p class="font-weight-bold error--text ma-0">
            Validation failed for the following fields:
          </p>
          <p class="error--text ma-0">
            Please fix the following errors and upload again.
          </p>
          <v-list dense>
            <v-list-item
              v-for="(error, idx) in validationMessage.errors"
              :key="idx"
              two-line
            >
              <v-list-item-icon class="mr-0 mt-4">
                <v-icon
                  color="error"
                  class="mr-2"
                >
                  $faExclamationCircle
                </v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ error.field }}</v-list-item-title>
                <v-list-item-subtitle>{{ error.detail }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
        <div class="button-group">
          <v-btn
            color="secondary"
            medium
            @click="uploadStep = 1"
          >
            Back
          </v-btn>
          <v-btn
            color="primary"
            medium
            :disabled="validationMessage.errors && !validationMessage.data.goodReservations"
            @click="uploadCSV"
          >
            Confirm Upload
          </v-btn>
          <v-btn
            text
            small
            @click="() => this.$emit('close-upload-modal')"
          >
            Cancel
          </v-btn>
        </div>
      </v-stepper-content>
    </v-stepper>
  </v-dialog>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { DateTime } from 'luxon'

  export default {
    name: 'ReservationsUploadForm',
    props: {
      uploadModal: {
        type: Boolean,
        default: false,
      },
    },
    data: () => ({
      valid: false,
      uploadedCSV: undefined,
      uploadStep: 1,
    }),
    computed: {
      ...mapGetters('Reservations', [
        'validationMessage',
      ]),
    },
    methods: {
      async validateCSV () {
        const payload = new FormData()
        payload.append('list', this.uploadedCSV)
        await this.$store.dispatch('Reservations/validateCSV', payload).then((response) => {
          if (this.validationMessage) {
            this.uploadStep = 2
          }
        })
      },
      async uploadCSV () {
        const payload = new FormData()
        payload.append('list', this.uploadedCSV)
        await this.$store.dispatch('Reservations/uploadCSV', payload).then((res) => {
          this.$emit('file-uploaded')
        })
      },
      formatDate (dateTime, timezone) {
        return DateTime.fromISO(dateTime).setZone(timezone).toLocaleString(DateTime.DATETIME_FULL_WITH_SECONDS)
      },
    },
  }
</script>
<style lang="scss">
.validation-table {
  &.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
    font-size: 11px !important;
  }
  &.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
    font-size: 12px;
  }
}
</style>
